// @i18n-ignore-file
// eslint-disable-next-line eslint-comments/disable-enable-pair
// https://lastbrand.atlassian.net/browse/ECOMP-4497
import { getAuthToken } from '@lastbrand/fe-common/universal-auth'
import {
  AxiosBasicCredentials,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  create as createHttpClient,
  AxiosError as HttpClientError,
  AxiosInstance as HttpClientInstance,
  AxiosResponse as HttpClientResponse,
  InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios'
import axiosRetry from 'axios-retry'
import { isBrowser } from 'react-device-detect'

import {
  API_ADDRESS_SERVICE_ORIGNIN,
  API_BFF_ORIGIN,
  API_OLD_ADDRESS_SERVICE_ORIGNIN,
  API_SF_RUNTIME_BASE_URL,
  API_SGTM_BASE_URL,
  API_STATE_META_ADDRESS_SERVICE_ORIGNIN,
  CONFIG_ADDRESS_VALIDATE_API,
  CONFIG_CART_SERVICES,
  CONFIG_INVENTORY_SERVICES,
  CONFIG_PRODUCT_CATALOG_ENDPOINT,
  CONFIG_PRODUCT_REVIEW_ENDPOINT,
  CONFIG_QUINCE_RETURN_SERVICE_API,
  CONFIG_QUINCE_SERIVCES_API,
  CONFIG_QUINCE_STORE_FRONT_BUILD_API,
  CONFIG_REPORTING_SERVICES,
  CONFIG_UPG_ENDPOINT,
  IS_PROD_BUILD,
  API_SF_BASE_URL,
  CONFIG_SAND_SERVICES,
  API_PRESENTATION_LAYER_SERVICE,
  CONFIG_CHECKOUT_SERVICES,
  API_SF_SAND_PRODUCT_BASE_URL,
  CONFIG_ZIPCODE_SERVICE_API,
  API_STORE_PROGRAM_SERVICE,
  CONFIG_QUINCE_ORDER_AGGREGATOR_SERVICES_API,
  CONFIG_ORDER_SERVICE_API,
  API_COMMUNICATION_PREFERENCE_ENDPOINT,
  API_COMMS_PLATFORM_ENDPOINT,
  CONFIG_REGIONAL_INVENTORY_SERVICES,
} from '@src/config/env'
import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'
import {
  CANARY_GROUP_ID_HEADER,
  LB_USER_ID_HEADER,
  PERSONALISATION_ENABLED_HEADER,
} from '@src/constants/api.headers.constants'
import { IExperimentLoggingData } from '@src/experiments/experiments.types'
import { statsigExperimentManager } from '@src/experiments/statsig-integration/statsig-experiment.manager'
import { FEATURE_HEADERS, ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { getPersonalisationAllowedFromCookies } from '@src/quince-site-common/src/utils/personalisation-consent'
import { getB2BHeaders, getStoreProgramIdFromStorage } from '@src/utils/b2b/b2b.utils'
import { getCanaryGroupId } from '@src/utils/canary-group-id'
import {
  getHeadersWithContentVersion,
  getContentVersionOnClient,
  getLocalisationHeaders,
} from '@src/utils/general.utils'

import { getIsNativeViewClient } from '../helpers/getIsNativeViewClient'
import { getCheckoutAuthCodeFromHeader } from '../services/cartCheckoutService/checkout.service.helper'
import { getCartAuthCodeFromHeader } from '../services/cartService/cart.service.helper'
import paymentDataHolder from '../services/unified-payment-gateway/payment-data-holder'

import { applyIdTokenAuthHeader } from './apply-id-token-auth-header'
import AuthUtil from './authUtil'
import getCookie from './getCookie'
import { getContentProductsEnv } from './getEnvironmentParam'
import getFormattedUrlForEnv from './getFormattedUrlForEnv/getFormattedUrlForEnv'
import { getlbUserId } from './getlb-user-id'
import isBrowserCheck from './isBrowser'
import { applySandRegionHeaders } from './sand-region-headers'

const authInterceptor = async (config: InternalAxiosRequestConfig) => {
  const token = await getAuthToken()
  if (token) {
    config.headers.Authorization = token
  }
  return config
}

// attach Cognito id token as Bearer when present.
export async function applyBearerTokenFromUserSession(request: InternalAxiosRequestConfig) {
  const userSession = await AuthUtil.getUserSession()
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken.jwtToken}`
  }
  return request
}

class BaseHttpClient {
  private readonly client: HttpClientInstance

  constructor(endPoint: string, basicAuth?: AxiosBasicCredentials, skipAuth = false) {
    this.client = createHttpClient({
      baseURL: endPoint,
    })
    this.client.interceptors.request.use(request => {
      request.auth = basicAuth

      // Set the CONTENT_VERSION_HEADER
      const contentVersion = getContentVersionOnClient()
      if (contentVersion) {
        request.headers = getHeadersWithContentVersion(request.headers) as AxiosRequestHeaders
      }
      const localisationHeaders = getLocalisationHeaders(request.baseURL)
      const b2bHeaders = getB2BHeaders(request.baseURL)
      const canaryGroupId = getCanaryGroupId()
      // EBB-393 (Copilot review, PR #8227): these three are auto-attached *defaults* derived from
      // cookies/localStorage — spread them first so a header a caller set explicitly on this
      // request (e.g. an SSR-sourced x-store-program-id) always wins, instead of a stale
      // localStorage value silently overwriting it after a program switch on the same origin.
      request.headers = {
        ...b2bHeaders,
        ...localisationHeaders,
        ...(canaryGroupId && { [CANARY_GROUP_ID_HEADER]: canaryGroupId }),
        ...request.headers,
      } as AxiosRequestHeaders
      return request
    })

    this.client.interceptors.response.use(null, async function (error) {
      const isUnauthRes = error.response?.status === 401 || error.response?.status === 403
      if (IS_PROD_BUILD && isUnauthRes && isBrowser && !skipAuth) {
        await window.logoutUser?.(false, true)
      }
      return Promise.reject(error instanceof Error ? error : new Error(String(error)))
    })
  }

  addRequestInterceptor(
    requestInterceptor: (
      value: InternalAxiosRequestConfig,
      // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
  ) {
    this.client.interceptors.request.use(requestInterceptor)
  }

  addResponseInterceptor(
    responseInterceptor: (
      value: AxiosResponse,
      // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    ) => AxiosResponse | Promise<AxiosResponse>,
  ) {
    this.client.interceptors.response.use(responseInterceptor)
  }

  addRetry({ retryCount, delay } = { retryCount: 1, delay: 1000 }) {
    axiosRetry(this.client, { retries: retryCount, retryDelay: () => delay })
  }

  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config)
  }

  post<T = any, D = any>(url: string, data: T, config?: AxiosRequestConfig) {
    return this.client.post<T, AxiosResponse<D>>(url, data, config)
  }

  put<T = any, D = any>(url: string, data: T, config?: AxiosRequestConfig) {
    return this.client.put<T, AxiosResponse<D>>(url, data, config)
  }

  patch<T = any, D = any>(url: string, data: T, config?: AxiosRequestConfig) {
    return this.client.patch<T, AxiosResponse<D>>(url, data, config)
  }

  delete<T = any, D = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T, AxiosResponse<D>>(url, config)
  }
}

class StorefrontRuntimeHttpClient extends BaseHttpClient {
  // Passing new website flag always for storefront apis.
  override get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const updatedConfig = config ?? {}
    const params = updatedConfig.params ?? {}
    updatedConfig.params = {
      ...params,
      isNewWebsite: 'true',
    }
    return super.get(url, updatedConfig)
  }
}

class StorefrontBuildTimeHttpClient extends BaseHttpClient {
  // Passing new website flag always for storefront apis.
  override get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const updatedConfig = config ?? {}
    const params = updatedConfig.params ?? {}
    updatedConfig.params = {
      ...params,
      isNewWebsite: 'true',
    }
    return super.get(url, updatedConfig)
  }
}

const productCatalogHttpClient = new BaseHttpClient(CONFIG_PRODUCT_CATALOG_ENDPOINT)

const productReviewHttpClient = new BaseHttpClient(CONFIG_PRODUCT_REVIEW_ENDPOINT)

const storefrontBuildHttpClient = new StorefrontBuildTimeHttpClient(CONFIG_QUINCE_STORE_FRONT_BUILD_API)

const storefrontSandProductServiceHttpClient = new BaseHttpClient(API_SF_SAND_PRODUCT_BASE_URL)
const storefrontSandProductServiceAuthHttpClient = new BaseHttpClient(API_SF_SAND_PRODUCT_BASE_URL)

storefrontSandProductServiceAuthHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `${userSession.idToken.jwtToken}`
  }
  return request
})

const reportingServicesHttpClient = new BaseHttpClient(CONFIG_REPORTING_SERVICES)
const reportingServicesStagingHttpClient = new BaseHttpClient('https://api-staging.onequince.com')

const reportingServicesHttpClientWithoutRetry = new BaseHttpClient(CONFIG_REPORTING_SERVICES)
const reportingTradeServicesHttpClient = new BaseHttpClient(CONFIG_REPORTING_SERVICES)

const sandServicesHttpClient = new BaseHttpClient(CONFIG_SAND_SERVICES)

sandServicesHttpClient.addRequestInterceptor(async request => {
  const storeProgramId = getStoreProgramIdFromStorage()
  if (storeProgramId) {
    const userSession = await AuthUtil.getUserSession()
    if (userSession?.idToken?.jwtToken) {
      request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
    }
  }

  // Locale-aware consent gate — UK locales require explicit functional opt-in,
  // US/CA default to allowed. See utils/personalisation-consent.ts.
  request.headers[PERSONALISATION_ENABLED_HEADER] = getPersonalisationAllowedFromCookies() ? 'true' : 'false'

  // STRFRNT-16796: guarantee a request-origin region on SAND calls (cookie → server-rendered
  // locale → default) and report when a fallback was needed. See sand-region-headers.ts.
  applySandRegionHeaders(request)

  return request
})

reportingServicesHttpClient.addRetry({
  retryCount: 1,
  delay: 60 * 1000, // 60 seconds
})

reportingTradeServicesHttpClient.addRetry({
  retryCount: 1,
  delay: 60 * 1000, // 60 seconds
})

const storefrontRuntimeHttpClient = new StorefrontRuntimeHttpClient(API_SF_RUNTIME_BASE_URL, undefined)
storefrontRuntimeHttpClient.addRetry({
  retryCount: 2,
  delay: 500, // .5s
})

storefrontRuntimeHttpClient.addRequestInterceptor(request => {
  request.url = getFormattedUrlForEnv(request.url)
  return request
})

const quinceServicesHttpClient = new BaseHttpClient(CONFIG_QUINCE_SERIVCES_API, undefined)
const quinceServicesPublicHttpClient = new BaseHttpClient(CONFIG_QUINCE_SERIVCES_API, undefined)
const quinceOrderAggregatorServiceHttpClient = new BaseHttpClient(
  CONFIG_QUINCE_ORDER_AGGREGATOR_SERVICES_API,
  undefined,
)
const quinceTrustAndSafetyServiceHttpClient = new BaseHttpClient(API_ADDRESS_SERVICE_ORIGNIN, undefined, true)
const quinceReturnServiceHttpClient = new BaseHttpClient(CONFIG_QUINCE_RETURN_SERVICE_API, undefined)

const storefrontZipcodeServicesHttpClient = new BaseHttpClient(CONFIG_ZIPCODE_SERVICE_API)

storefrontZipcodeServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = userSession?.accessToken?.jwtToken
  return request
})

const storefrontCartServicesHttpClient = new BaseHttpClient(CONFIG_CART_SERVICES)

storefrontCartServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()

  // send out of stock cart experiment bucket
  const experimentBuckets = statsigExperimentManager.getExposedBuckets()

  let outOfStockCartExperimentBucket = experimentBuckets.find(
    exp => exp.experimentName === STATSIG_EXPERIMENTS.OOS_CART,
  )?.bucketName

  // if the experiment bucket is not found, check local storage as for force bucketing logics on the initial load
  // this is to ensure that the experiment bucket is always set on the initial load if statsig is not ready
  if (!outOfStockCartExperimentBucket && isBrowser) {
    const existingBuckets = JSON.parse(
      // eslint-disable-next-line @quince/no-direct-localstorage
      localStorage.getItem('statsigExperimentBuckets') ?? '[]',
    ) as IExperimentLoggingData[]
    // eslint-disable-next-line @quince/no-direct-localstorage
    const forceBuckets = JSON.parse(localStorage.getItem('statsigForcedBuckets') ?? '{}')
    outOfStockCartExperimentBucket =
      existingBuckets.find(exp => exp.experimentName === STATSIG_EXPERIMENTS.OOS_CART)?.bucketName ??
      forceBuckets[STATSIG_EXPERIMENTS.OOS_CART]
  }

  if (outOfStockCartExperimentBucket) {
    request.headers['X-Buckets'] = JSON.stringify({
      [STATSIG_EXPERIMENTS.OOS_CART]: outOfStockCartExperimentBucket,
    })
  }

  const authCode = getCartAuthCodeFromHeader()
  if (authCode) {
    request.headers['auth-code'] = authCode
  }
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
  }
  return request
})

const storefrontCheckoutServicesHttpClient = new BaseHttpClient(CONFIG_CHECKOUT_SERVICES)

storefrontCheckoutServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()

  // send out of stock cart experiment bucket
  const experimentBuckets = statsigExperimentManager.getExposedBuckets()

  let outOfStockCartExperimentBucket = experimentBuckets.find(
    exp => exp.experimentName === STATSIG_EXPERIMENTS.OOS_CART,
  )?.bucketName

  // if the experiment bucket is not found, check local storage as for force bucketing logics on the initial load
  // this is to ensure that the experiment bucket is always set on the initial load if statsig is not ready
  if (!outOfStockCartExperimentBucket && isBrowser) {
    const existingBuckets = JSON.parse(
      // eslint-disable-next-line @quince/no-direct-localstorage
      localStorage.getItem('statsigExperimentBuckets') ?? '[]',
    ) as IExperimentLoggingData[]
    // eslint-disable-next-line @quince/no-direct-localstorage
    const forceBuckets = JSON.parse(localStorage.getItem('statsigForcedBuckets') ?? '{}')
    outOfStockCartExperimentBucket =
      existingBuckets.find(exp => exp.experimentName === STATSIG_EXPERIMENTS.OOS_CART)?.bucketName ??
      forceBuckets[STATSIG_EXPERIMENTS.OOS_CART]
  }

  const exposedExperiments = statsigExperimentManager.getExposedBuckets()
  const eddExpBucket = exposedExperiments.find(experiment => experiment.experimentName === STATSIG_EXPERIMENTS.EDD)

  let expHeaders = {}
  if (eddExpBucket) {
    expHeaders = {
      ...expHeaders,
      [STATSIG_EXPERIMENTS.EDD]: eddExpBucket.bucketName,
    }
  }

  if (outOfStockCartExperimentBucket) {
    expHeaders = {
      ...expHeaders,
      [STATSIG_EXPERIMENTS.OOS_CART]: outOfStockCartExperimentBucket,
    }
  }

  if (Object.keys(expHeaders).length > 0) {
    request.headers['X-Buckets'] = JSON.stringify(expHeaders)
  }

  const { checkoutAuthCode = '', cartAuthCode = '' } = getCheckoutAuthCodeFromHeader()
  if (checkoutAuthCode) {
    request.headers['auth-code'] = checkoutAuthCode
  }
  if (cartAuthCode) {
    request.headers['cart-auth-code'] = cartAuthCode
  }
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
  }

  const X_INGRESS_VALUES = {
    HEADER: 'x-ingress',
    PDP: 'PDP',
    CART: 'cart',
    CHECKOUT: 'checkout',
  }

  request.headers[X_INGRESS_VALUES.HEADER] = X_INGRESS_VALUES.CART
  return request
})

const fullCheckoutServicesHttpClient = new BaseHttpClient(CONFIG_CHECKOUT_SERVICES)
fullCheckoutServicesHttpClient.addRequestInterceptor(authInterceptor)
fullCheckoutServicesHttpClient.addRequestInterceptor((config: InternalAxiosRequestConfig) => {
  if (!isBrowserCheck()) {
    return config
  }

  const FULL_CHECKOUT_WHITELISTED_EXPERIMENTS: string[] = [
    STATSIG_EXPERIMENTS.EDD,
    STATSIG_EXPERIMENTS.SECOND_RETURNS_CHARGING,
  ]
  const FULL_CHECKOUT_APP_WEBVIEW_EXPERIMENT_OVERRIDES: Record<string, string | undefined> = {
    [STATSIG_EXPERIMENTS.EDD]: 'groupB',
    [STATSIG_EXPERIMENTS.SECOND_RETURNS_CHARGING]: 'Control',
  }

  const statsigExposedBuckets = statsigExperimentManager.getExposedBuckets()
  let experimentsMap: Record<string, string | undefined> = {}

  statsigExposedBuckets.forEach(({ experimentName, bucketName }) => {
    if (FULL_CHECKOUT_WHITELISTED_EXPERIMENTS.includes(experimentName)) {
      experimentsMap[experimentName] = bucketName
    }
  })

  if (getIsNativeViewClient()) {
    experimentsMap = { ...experimentsMap, ...FULL_CHECKOUT_APP_WEBVIEW_EXPERIMENT_OVERRIDES }
  }

  if (Object.keys(experimentsMap).length > 0) {
    config.headers['X-Buckets'] = JSON.stringify(experimentsMap)
  }

  return config
})

const storefrontBaseServicesHttpClient = new BaseHttpClient(API_SF_BASE_URL)

storefrontBaseServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
  }
  return request
})

const storefrontInventoryServicesHttpClient = new BaseHttpClient(CONFIG_INVENTORY_SERVICES)
storefrontInventoryServicesHttpClient.addRequestInterceptor(authInterceptor)
const storefrontRegionalInventoryServicesHttpClient = new BaseHttpClient(CONFIG_REGIONAL_INVENTORY_SERVICES)
storefrontRegionalInventoryServicesHttpClient.addRequestInterceptor(authInterceptor)

quinceServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  return request
})
quinceOrderAggregatorServiceHttpClient.addRequestInterceptor(applyBearerTokenFromUserSession)
quinceTrustAndSafetyServiceHttpClient.addRequestInterceptor(applyBearerTokenFromUserSession)

quinceServicesPublicHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession.idToken?.jwtToken}`
  }
  return request
})

const unifiedPaymentGatewayClient = new BaseHttpClient(CONFIG_UPG_ENDPOINT)
unifiedPaymentGatewayClient.addRequestInterceptor(authInterceptor)

const AUTH_CODE_HEADER_KEY = 'auth-code'
const unifiedPaymentGatewayHttpClient = new BaseHttpClient(CONFIG_UPG_ENDPOINT)
unifiedPaymentGatewayHttpClient.addRequestInterceptor(authInterceptor)
unifiedPaymentGatewayHttpClient.addRequestInterceptor(request => {
  request.headers[LB_USER_ID_HEADER] = getCookie(ANALYTIC_COOKIES.LB_USER_ID)
  return request
})
unifiedPaymentGatewayHttpClient.addRequestInterceptor(config => {
  config.headers[AUTH_CODE_HEADER_KEY] = paymentDataHolder.getSessionAuthCode()
  return config
})
unifiedPaymentGatewayHttpClient.addResponseInterceptor(response => {
  const sessionAuthCode = response.headers[AUTH_CODE_HEADER_KEY]
  const sessionId = response.data?.id
  if (sessionAuthCode && sessionId) {
    paymentDataHolder.updatePaymentData({
      sessionAuthCode,
      sessionId,
    })
  }
  return response
})

const orderServiceClient = new BaseHttpClient(CONFIG_ORDER_SERVICE_API)
orderServiceClient.addRequestInterceptor(authInterceptor)

quinceReturnServiceHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  return request
})

reportingServicesHttpClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  if (userSession?.idToken?.jwtToken) {
    request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  }
  return request
})

export const storefrontRegionsHttpClient = new BaseHttpClient(`${CONFIG_REPORTING_SERVICES}/region-management-service`)

//const stampedHttpClient = new BaseHttpClient('https://stamped.io/api/', undefined);

const addressValidationClient = new BaseHttpClient(`${CONFIG_ADDRESS_VALIDATE_API}/address/v1`)

// Customer Service V2: customer-facing endpoints are unified under /customer-services/customers
// (addresses, name, dob, settings, …).
// TODO(backend/env): host reuses API_OLD_ADDRESS_SERVICE_ORIGNIN. Consider a dedicated
// NEXT_PUBLIC_CUSTOMER_SERVICES origin once V2 has its own host.
const customerServicesV2Client = new BaseHttpClient(`${API_OLD_ADDRESS_SERVICE_ORIGNIN}/customer-services/customers`)
customerServicesV2Client.addRequestInterceptor(authInterceptor)

const addressServiceClient = new BaseHttpClient(API_ADDRESS_SERVICE_ORIGNIN)
addressServiceClient.addRequestInterceptor(authInterceptor)

const addressStateMetaServiceClient = new BaseHttpClient(API_STATE_META_ADDRESS_SERVICE_ORIGNIN)
addressStateMetaServiceClient.addRequestInterceptor(authInterceptor)

const sgtmServiceClient = new BaseHttpClient(API_SGTM_BASE_URL)

// this is used only in client side, s2s uses next fetch api
const bffClient = new BaseHttpClient(API_BFF_ORIGIN)
bffClient.addRequestInterceptor(async request => {
  const userSession = await AuthUtil.getUserSession()
  // Set the LB_USER_ID_HEADER
  const lbUserId = getlbUserId()
  if (lbUserId) {
    request.headers[LB_USER_ID_HEADER] = lbUserId
  }

  if (userSession) {
    request.headers.Authorization = `Bearer ${userSession?.idToken?.jwtToken}`
  }

  request.headers[FEATURE_HEADERS.CONTENT_ENV] = getContentProductsEnv()

  // Forward user's functional consent decision to all downstream BFF requests.
  // Locale-aware: UK / countryCompliant locales require explicit opt-in
  // (cookie === "true"); pre-consent UK users emit `false`. US / non-compliant
  // locales default to `true` — they never see TrustArc, so the cookie is
  // permanently absent. See utils/personalisation-consent.ts for the rule.
  request.headers[PERSONALISATION_ENABLED_HEADER] = getPersonalisationAllowedFromCookies() ? 'true' : 'false'

  return request
})

const presentationServiceClient = new BaseHttpClient(API_PRESENTATION_LAYER_SERVICE)
presentationServiceClient.addRequestInterceptor(request => {
  // Forward the user's functional-consent decision to the presentation-layer
  // service, same as the BFF/SAND clients. Locale-aware: UK requires explicit
  // opt-in (cookie === "true"); US/CA default to allowed. See
  // utils/personalisation-consent.ts.
  request.headers[PERSONALISATION_ENABLED_HEADER] = getPersonalisationAllowedFromCookies() ? 'true' : 'false'
  return request
})
const storeProgramServiceClient = new BaseHttpClient(API_STORE_PROGRAM_SERVICE)
const communicationPreferenceServiceClient = new BaseHttpClient(API_COMMUNICATION_PREFERENCE_ENDPOINT)
const commsPlatformServiceClient = new BaseHttpClient(API_COMMS_PLATFORM_ENDPOINT)

// Attach the Cognito idToken when a session exists (best-effort), mirroring the native app's comms
// client. The header is omitted when there is no session — the request still proceeds.
commsPlatformServiceClient.addRequestInterceptor(applyIdTokenAuthHeader)

const isHttpClientError = isAxiosError

export {
  addressServiceClient,
  addressValidationClient,
  bffClient,
  customerServicesV2Client,
  isHttpClientError,
  productCatalogHttpClient,
  productReviewHttpClient,
  quinceReturnServiceHttpClient,
  quinceServicesHttpClient,
  quinceOrderAggregatorServiceHttpClient,
  quinceTrustAndSafetyServiceHttpClient,
  quinceServicesPublicHttpClient,
  reportingServicesHttpClient,
  reportingServicesHttpClientWithoutRetry,
  reportingServicesStagingHttpClient,
  reportingTradeServicesHttpClient,
  storefrontBuildHttpClient,
  storefrontCartServicesHttpClient,
  storefrontInventoryServicesHttpClient,
  storefrontRegionalInventoryServicesHttpClient,
  storefrontRuntimeHttpClient,
  storefrontZipcodeServicesHttpClient,
  sgtmServiceClient,
  addressStateMetaServiceClient,
  unifiedPaymentGatewayClient,
  orderServiceClient,
  storefrontBaseServicesHttpClient,
  sandServicesHttpClient,
  presentationServiceClient,
  storefrontCheckoutServicesHttpClient,
  fullCheckoutServicesHttpClient,
  storefrontSandProductServiceHttpClient,
  storefrontSandProductServiceAuthHttpClient,
  storeProgramServiceClient,
  unifiedPaymentGatewayHttpClient,
  communicationPreferenceServiceClient,
  commsPlatformServiceClient,
}

export type { HttpClientResponse, HttpClientError }
